// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Button, Collapse, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseAvatarGroup, Loading, UnderDevelopment } from '~/components/common';
import { LabelValueSchedule, PickSlipFormModal, SplitFormModal } from '~/components/module/order';
// Others
import { IFormJob } from '~/utils/interface/jobs';
import { updateJob } from '~/thunks/job/jobThunk';
import { StorageEnum } from '~/utils/enum/base';
import { getFullName } from '~/utils/helper/common';
import { IBodyUpdate } from '~/utils/interface/common';
import { orderActions } from '~/thunks/order/orderSlice';
import { IScheduleList } from '~/utils/interface/schedule';
import { useAppDispatch } from '~/utils/redux/hooks';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { BusinessTypeEnum, ColorTypeEnum, RoleEnum, ScheduleSplitType } from '~/utils/enum/common';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { revertScheduleToJob } from '~/thunks/schedule/scheduleThunk';
// Styles, images, icons
import styles from './ItemSchedule.module.scss';
import { icons } from '~/assets';

type Props = {
  item: IScheduleList;
  onShowModal?: (isShowModal: boolean) => void;
};

const cx = classNames.bind(styles);

const ItemSchedule = (props: Props) => {
  //#region Destructuring Props
  const { item, onShowModal } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/schedule', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowPickSplitModal, setIsShowPickSplitModal] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isOpenSplitFormModal, setIsOpenSplitFormModal] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleCollapseExpand = () => {
    setIsOpen(!isOpen);
  };

  const handleShowPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleChangeColor = (color: ColorTypeEnum) => {
    handleClosePopover();
    setIsLoading(true);

    const dataUpdate: IBodyUpdate<IFormJob> = {
      id: item.businessId,
      body: {
        color: color,
      },
    };

    dispatch(updateJob(dataUpdate))
      .unwrap()
      .then(() => {
        dispatch(scheduleActions.setRefreshScheduleList(true));
      })
      .catch((_error) => {})
      .finally(() => setIsLoading(false));
  };

  const handleRevertScheduleToJob = () => {
    setIsLoading(true);

    dispatch(revertScheduleToJob(item?.id))
      .unwrap()
      .then(() => {
        dispatch(orderActions.setRefreshOrderEventList(true));
        dispatch(scheduleActions.setRefreshScheduleList(true));
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleShowPickSplitModal = () => {
    setIsShowPickSplitModal(!isShowPickSplitModal);
  };

  const handleShowSplitFormModal = () => {
    onShowModal?.(!isOpenSplitFormModal);
    setIsOpenSplitFormModal(!isOpenSplitFormModal);
  };

  const handleNavigateDetailOrder = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.detailOrder(item?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.detailOrder(item?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.detailOrder(item?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.detailOrder(item?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.detailOrder(item?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.detailOrder(item?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.detailOrder(item?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.detailOrder(item?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.detailOrder(item?.orderId || EMPTY_STRING));
        break;
    }
  };

  const handleNavigateDetailJob = () => {
    if (item?.businessType === BusinessTypeEnum.JOB) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(adminRouteAbsolute.detailJob(item?.businessId || EMPTY_STRING));
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(salesAdminRouteAbsolute.detailJob(item?.businessId || EMPTY_STRING));
          break;
        case RoleEnum.SALES_PERSON:
          navigate(salesPersonRouteAbsolute.detailJob(item?.businessId || EMPTY_STRING));
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(operationsManagerRouteAbsolute.detailJob(item?.businessId || EMPTY_STRING));
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(warehouseLeadRouteAbsolute.detailJob(item?.businessId || EMPTY_STRING));
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(warehouseStaffRouteAbsolute.detailJob(item?.businessId || EMPTY_STRING));
          break;
        case RoleEnum.FOREMAN:
          navigate(foremanRouteAbsolute.detailJob(item?.businessId || EMPTY_STRING));
          break;
        case RoleEnum.CREW:
          navigate(crewRouteAbsolute.detailJob(item?.businessId || EMPTY_STRING));
          break;
        case RoleEnum.LOADER:
          navigate(loaderRouteAbsolute.detailJob(item?.businessId || EMPTY_STRING));
          break;
      }
    }
  };
  //#endregion Handle Function

  return (
    <Box id='itemScheduleComponent' className={cx('container', item.color)}>
      <Box className={cx('viewItem')}>
        <Stack flexDirection={'row'} gap={1} alignItems={'center'}>
          <Tooltip arrow placement='top' title={t('schedule_item_reschedule')}>
            <IconButton onClick={handleRevertScheduleToJob}>
              <Avatar
                className={cx('iconBack')}
                src={icons.commonIconBackSchedule}
                alt={t('common_alt_img', { ns: 'common/base' })}
              />
            </IconButton>
          </Tooltip>

          <Typography className={cx('title')} onClick={handleNavigateDetailJob}>
            {item.title}
          </Typography>
        </Stack>

        <IconButton onClick={handleCollapseExpand} size={'small'}>
          <Avatar
            className={cx('iconDropdown', isOpen && 'arrowIconOpen', 'arrowIcon')}
            src={icons.commonIconDropdown}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </Box>

      <Box className={cx('content')}>
        <Stack flexDirection={'row'} gap={2} onClick={handleNavigateDetailOrder}>
          <Typography className={cx('labelType')}>{t('schedule_item_order_label')}</Typography>

          <Typography className={cx('valueTypeOrder')}>{item?.orderNumber || item?.orderId || EMPTY_VALUE}</Typography>
        </Stack>

        <LabelValueSchedule
          typeStyle='small'
          label={t('schedule_item_client_label')}
          value={getFullName({ firstName: item?.client?.firstName, lastName: item?.client?.lastName }) || EMPTY_VALUE}
        />

        {item?.isDelivery && (
          <Stack flexDirection={'row'} gap={2}>
            <Typography className={cx('labelType')}>{t('schedule_item_type_label')}</Typography>

            <Typography className={cx('valueTypeDelivery')}>{t('schedule_item_delivery')}</Typography>
          </Stack>
        )}

        {item?.isPickup && (
          <Stack flexDirection={'row'} gap={2}>
            <Typography className={cx('labelType')}>{t('schedule_item_type_label')}</Typography>

            <Typography className={cx('valueTypePickup')}>{t('schedule_item_pickup')}</Typography>
          </Stack>
        )}

        <LabelValueSchedule typeStyle='small' value={item?.deliveryAddress?.address} />
        <Collapse in={isOpen} timeout='auto' unmountOnExit>
          <Stack flexDirection={'row'} gap={2} alignItems={'center'}>
            <Typography className={cx('labelType')}>{t('schedule_item_foreman_label')}</Typography>

            {item?.foreman ? (
              <BaseAvatarGroup
                size={20}
                avatars={[
                  {
                    fullName:
                      getFullName({ firstName: item?.foreman?.firstName, lastName: item?.foreman?.lastName }) ||
                      EMPTY_VALUE,
                  },
                ]}
              />
            ) : (
              <Typography className={cx('labelType')}>{EMPTY_VALUE}</Typography>
            )}
          </Stack>

          <LabelValueSchedule
            typeStyle='small'
            label={t('schedule_item_driver_label')}
            value={getFullName({ firstName: item?.driver?.firstName, lastName: item?.driver?.lastName }) || EMPTY_VALUE}
          />
          <LabelValueSchedule
            typeStyle='small'
            label={t('schedule_item_truck_label')}
            value={item?.truck?.name || EMPTY_VALUE}
          />

          <Stack flexDirection={'row'} gap={2} alignItems={'center'}>
            <Typography className={cx('labelType')}>{t('schedule_item_crew_label')}</Typography>

            {item?.crews && item?.crews?.length > 0 ? (
              <BaseAvatarGroup
                size={20}
                avatars={item?.crews?.map((item) => ({
                  fullName: getFullName({ firstName: item?.firstName, lastName: item?.lastName }) || EMPTY_VALUE,
                }))}
              />
            ) : (
              <Typography className={cx('labelType')}>{EMPTY_VALUE}</Typography>
            )}
          </Stack>

          <Stack flexDirection={'row'} gap={2} alignItems={'center'}>
            <Typography className={cx('labelType')}>{t('schedule_item_loader_label')}</Typography>

            {item?.loader && item?.loader?.length > 0 ? (
              <BaseAvatarGroup
                size={20}
                avatars={item?.loader?.map((item) => ({
                  fullName: getFullName({ firstName: item?.firstName, lastName: item?.lastName }) || EMPTY_VALUE,
                }))}
              />
            ) : (
              <Typography className={cx('labelType')}>{EMPTY_VALUE}</Typography>
            )}
          </Stack>
        </Collapse>
        <LabelValueSchedule
          typeStyle='small'
          label={t('schedule_item_est_hours_label')}
          value={`${item.estimate} ${t('schedule_item_mins_value')}`}
        />
      </Box>

      <Stack direction={'row'} alignItems={'center'} justifyContent={'end'} spacing={1}>
        <Stack direction={'row'} alignItems={'center'}>
          <Button className={cx('buttonMenu')} variant='contained' onClick={handleShowSplitFormModal}>
            {t('schedule_item_button_split')}
          </Button>

          <Tooltip arrow placement='top' title={t('schedule_item_change_color_button')}>
            <IconButton onClick={handleShowPopover}>
              <Avatar
                className={cx('pickColorIcon')}
                src={icons.commonIconChangeColor}
                alt={t('common_alt_img', { ns: 'common/base' })}
              />
            </IconButton>
          </Tooltip>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={(event) => {
              handleClosePopover();
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              className: cx('paper'),
            }}
          >
            <Box className={cx('popoverContent')}>
              <Typography className={cx('titleMenu')}>{t('schedule_change_color_title')}</Typography>

              <Box className={cx('menuItems')}>
                <Button
                  variant='outlined'
                  className={cx('menuItem', 'redColor', item.color === ColorTypeEnum.RED && 'active')}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleChangeColor(ColorTypeEnum.RED);
                  }}
                >
                  <Box className={cx('menuItemColor', 'redColor', item.color === ColorTypeEnum.RED && 'active')} />
                  <Typography className={cx('menuItemTitle')}>{t('schedule_change_color_red_label')}</Typography>
                </Button>

                <Button
                  variant='outlined'
                  className={cx('menuItem', 'blueColor', item.color === ColorTypeEnum.BLUE && 'active')}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleChangeColor(ColorTypeEnum.BLUE);
                  }}
                >
                  <Box className={cx('menuItemColor', 'blueColor')} />
                  <Typography className={cx('menuItemTitle')}>{t('schedule_change_color_blue_label')}</Typography>
                </Button>
              </Box>

              <Box className={cx('menuItems')}>
                <Button
                  variant='outlined'
                  className={cx('menuItem', 'greenColor', item.color === ColorTypeEnum.GREEN && 'active')}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleChangeColor(ColorTypeEnum.GREEN);
                  }}
                >
                  <Box className={cx('menuItemColor', 'greenColor')} />
                  <Typography className={cx('menuItemTitle')}>{t('schedule_change_color_green_label')}</Typography>
                </Button>

                <Button
                  variant='outlined'
                  className={cx('menuItem', 'yellowColor', item.color === ColorTypeEnum.YELLOW && 'active')}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleChangeColor(ColorTypeEnum.YELLOW);
                  }}
                >
                  <Box className={cx('menuItemColor', 'yellowColor')} />
                  <Typography className={cx('menuItemTitle')}>{t('schedule_change_color_yellow_label')}</Typography>
                </Button>
              </Box>
            </Box>
          </Popover>

          <Tooltip arrow placement='top' title={t('schedule_item_pick_slip_tooltip')}>
            <IconButton onClick={handleShowPickSplitModal}>
              <Avatar
                className={cx('pickColorIcon')}
                src={icons.commonIconPickColor}
                alt={t('common_alt_img', { ns: 'common/base' })}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isShowPickSplitModal && <PickSlipFormModal jobId={item.businessId} onClose={handleShowPickSplitModal} />}

      {isOpenSplitFormModal && (
        <SplitFormModal item={item} onClose={handleShowSplitFormModal} type={ScheduleSplitType.VEHICLE_SCHEDULE} />
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default ItemSchedule;
